// Fires analytics events to Google Analytics (gtag) and Meta Pixel (fbq) safely.

// Anonymous page-view beacon to our own backend (powers the dev analytics).
export function trackPageview(path) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) return;
    fetch(`${base}/api/track/pageview`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path: path || window.location.pathname || "/" }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) { /* noop */ }
}
export function track(eventName, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  } catch (e) { /* noop */ }
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      const standard = { Lead: "Lead", Schedule: "Schedule", Contact: "Contact" };
      if (params.fb_event && standard[params.fb_event]) {
        window.fbq("track", standard[params.fb_event], params);
      } else {
        window.fbq("trackCustom", eventName, params);
      }
    }
  } catch (e) { /* noop */ }
}

// Fire an event only once per key (per page session).
const _fired = new Set();
export function trackOnce(key, eventName, params = {}) {
  if (_fired.has(key)) return;
  _fired.add(key);
  track(eventName, params);
}

// Build a short human label for a clicked element.
function labelFor(el) {
  if (!el) return "";
  const aria = el.getAttribute && el.getAttribute("aria-label");
  const testid = el.getAttribute && el.getAttribute("data-testid");
  let text = (el.innerText || el.textContent || "").trim().replace(/\s+/g, " ");
  if (text.length > 60) text = text.slice(0, 60);
  return aria || text || testid || (el.tagName ? el.tagName.toLowerCase() : "");
}

// Track every meaningful click made by the visitor.
export function initClickTracking() {
  if (typeof document === "undefined") return;
  if (window.__cfmClickTracking) return;
  window.__cfmClickTracking = true;

  document.addEventListener(
    "click",
    (e) => {
      const path = e.composedPath ? e.composedPath() : [e.target];
      // find nearest interactive element (button / link / has testid / role)
      let el = null;
      for (const node of path) {
        if (!node || !node.getAttribute) continue;
        const tag = node.tagName ? node.tagName.toLowerCase() : "";
        if (
          tag === "button" ||
          tag === "a" ||
          node.getAttribute("data-testid") ||
          node.getAttribute("role") === "button" ||
          tag === "input" ||
          tag === "select"
        ) {
          el = node;
          break;
        }
      }
      const target = el || e.target;
      if (!target || !target.getAttribute) return;

      const testid = target.getAttribute("data-testid") || "";
      const tag = target.tagName ? target.tagName.toLowerCase() : "";
      const href = tag === "a" ? target.getAttribute("href") || "" : "";
      // nearest section id for context
      let section = "";
      let p = target;
      while (p && p !== document.body) {
        if (p.tagName === "SECTION" && p.id) { section = p.id; break; }
        p = p.parentElement;
      }

      track("click", {
        click_label: labelFor(target),
        click_testid: testid,
        click_tag: tag,
        click_href: href,
        page_section: section,
      });
    },
    { capture: true, passive: true }
  );

  // Track when a visitor starts filling any form (first focus into it).
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target;
      if (!t || !t.closest) return;
      const tag = t.tagName ? t.tagName.toLowerCase() : "";
      if (!["input", "textarea", "select"].includes(tag)) return;
      const formEl = t.closest("form, [data-form-name]");
      if (!formEl) return;
      const name =
        formEl.getAttribute("data-form-name") ||
        formEl.getAttribute("data-testid") ||
        "form";
      trackOnce(`form_start:${name}`, "form_start", { form: name });
    },
    { capture: true, passive: true }
  );
}
